import { supabase } from '@/integrations/supabase/client';
import { HabitPerformance, HabitMovingAverage, PillarSlug } from '@/types';
import { handleDatabaseError } from '@/lib/errorHandler';

export const performanceService = {
  /**
   * Calcular e salvar média móvel para um hábito
   */
  async calculateMovingAverage(habitId: string, date: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('calculate_habit_moving_average', {
        p_habit_id: habitId,
        p_date: date
      });

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao calcular média móvel:', error);
    }
  },

  /**
   * Buscar média móvel de um hábito
   */
  async getMovingAverages(habitId: string, limit: number = 30): Promise<HabitMovingAverage[]> {
    try {
      const { data, error } = await supabase
        .from('habit_moving_averages')
        .select('*')
        .eq('habit_id', habitId)
        .order('date', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map(d => ({
        id: d.id,
        habitId: d.habit_id,
        date: d.date,
        ma7Days: d.ma_7_days,
        ma30Days: d.ma_30_days,
        trend: d.trend as 'improving' | 'stable' | 'declining',
        createdAt: d.created_at
      }));
    } catch (error) {
      handleDatabaseError(error);
      return [];
    }
  },

  /**
   * Buscar performance de um hábito
   */
  async getHabitPerformance(habitId: string): Promise<HabitPerformance | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('habit_performance')
        .select('*')
        .eq('habit_id', habitId)
        .eq('user_id', user.id)
        .maybeSingle(); // ✅ Usar maybeSingle() para evitar erro 406

      if (error) throw error;
      if (!data) return null; // Não encontrado é OK

      return {
        id: data.id,
        habitId: data.habit_id,
        userId: data.user_id,
        adherenceRate: data.adherence_rate,
        last7DaysCompleted: data.last_7_days_completed,
        lastAdjustmentDate: data.last_adjustment_date,
        adjustmentReason: data.adjustment_reason,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      handleDatabaseError(error);
      return null;
    }
  },

  /**
   * Atualizar ou criar performance de hábito
   */
  async updateHabitPerformance(
    habitId: string,
    adherenceRate: number,
    last7DaysCompleted: number
  ): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('habit_performance')
        .upsert({
          habit_id: habitId,
          user_id: user.id,
          adherence_rate: adherenceRate,
          last_7_days_completed: last7DaysCompleted,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'habit_id,user_id'
        });

      if (error) throw error;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Registrar ajuste de hábito
   */
  async logAdjustment(habitId: string, reason: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('habit_performance')
        .update({
          last_adjustment_date: new Date().toISOString(),
          adjustment_reason: reason
        })
        .eq('habit_id', habitId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Analisar se hábito precisa de ajuste
   */
  async analyzeForAutoAdjust(habitId: string): Promise<{
    needsAdjustment: boolean;
    reason?: string;
    suggestion?: 'reduce_frequency' | 'simplify' | 'pause';
  }> {
    try {
      const performance = await this.getHabitPerformance(habitId);
      if (!performance) return { needsAdjustment: false };

      const threshold = 40; // 40% de adesão ou menos precisa ajuste

      if (performance.adherenceRate < threshold) {
        return {
          needsAdjustment: true,
          reason: `Baixa adesão (${performance.adherenceRate.toFixed(0)}% nos últimos 7 dias)`,
          suggestion: performance.adherenceRate < 20 ? 'pause' : 
                     performance.adherenceRate < 30 ? 'simplify' : 'reduce_frequency'
        };
      }

      return { needsAdjustment: false };
    } catch (error) {
      console.error('Erro ao analisar ajuste:', error);
      return { needsAdjustment: false };
    }
  },

  /**
   * Buscar histórico de pontuações semanais
   */
  async getWeeklyPillarScores(
    userId: string, 
    limit: number = 8 // Últimas 8 semanas
  ): Promise<Array<{
    weekStart: string;
    weekEnd: string;
    pillar: string;
    score: number;
    method: string;
  }>> {
    try {
      const { data, error } = await supabase
        .from('weekly_pillar_scores')
        .select('*')
        .eq('user_id', userId)
        .order('week_start_date', { ascending: false })
        .limit(limit * 6); // 6 pilares por semana

      if (error) throw error;

      return (data || []).map(d => ({
        weekStart: d.week_start_date,
        weekEnd: d.week_end_date,
        pillar: d.pillar,
        score: d.score,
        method: d.calculation_method
      }));
    } catch (error) {
      handleDatabaseError(error);
      return [];
    }
  },

  /**
   * Calcular pontuação inteligente de um pilar
   * Considera: Hábitos (60%) + Metas (40%) + Consistência (bonus +10%)
   */
  calculatePillarScoreIntelligent(
    pillar: PillarSlug,
    habits: any[],
    goals: any[],
    weekDates: string[]
  ): { score: number; metadata: any } {
    // Filtrar hábitos do pilar (suporta tanto pillars[] quanto pilar)
    const pillarHabits = habits.filter(h => 
      h.active && (
        (h.pillars && h.pillars.includes(pillar)) ||
        h.pilar === pillar
      )
    );

    // Filtrar metas do pilar (suporta tanto pillars[] quanto pilar)
    const pillarGoals = goals.filter(g => 
      g.status === 'em_andamento' && (
        (g.pillars && g.pillars.includes(pillar)) ||
        g.pilar === pillar
      )
    );

    // === PARTE 1: HÁBITOS (60% do score) ===
    let habitsScore = 0;
    let habitsCompletionRate = 0;
    let totalPossible = 0;
    let totalCompleted = 0;
    let consistencyScore = 0;

    if (pillarHabits.length > 0) {
      pillarHabits.forEach(habit => {
        let habitCompleted = 0;
        let habitPossible = 0;
        let streak = 0;
        let maxStreak = 0;

        weekDates.forEach(date => {
          if (habit.frequency === 'diaria' || shouldHabitRunOnDate(habit, date)) {
            habitPossible++;
            if (habit.history && habit.history.includes(date)) {
              habitCompleted++;
              streak++;
              maxStreak = Math.max(maxStreak, streak);
            } else {
              streak = 0;
            }
          }
        });

        totalPossible += habitPossible;
        totalCompleted += habitCompleted;

        // Calcular consistência (streaks contam mais)
        if (habitPossible > 0) {
          const streakBonus = maxStreak / weekDates.length;
          consistencyScore += streakBonus;
        }
      });

      habitsCompletionRate = totalPossible > 0 ? totalCompleted / totalPossible : 0;
      habitsScore = habitsCompletionRate * 0.6; // 60% do score

      // Média de consistência entre hábitos
      consistencyScore = pillarHabits.length > 0 ? consistencyScore / pillarHabits.length : 0;
    }

    // === PARTE 2: METAS (40% do score) ===
    let goalsScore = 0;
    let goalsProgressRate = 0;
    let miniGoalsCompleted = 0;
    let miniGoalsTotal = 0;

    if (pillarGoals.length > 0) {
      pillarGoals.forEach(goal => {
        if (goal.miniGoals && goal.miniGoals.length > 0) {
          const completed = goal.miniGoals.filter((m: any) => m.completed).length;
          miniGoalsCompleted += completed;
          miniGoalsTotal += goal.miniGoals.length;

          // Bonus por proximidade do deadline (urgência aumenta importância)
          const now = new Date();
          const deadline = new Date(goal.deadline);
          const daysUntilDeadline = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysUntilDeadline > 0 && daysUntilDeadline <= 30) {
            // Bonus de urgência (0.1 para deadlines em até 30 dias)
            const urgencyBonus = 0.1 * (1 - daysUntilDeadline / 30);
            goalsProgressRate += urgencyBonus;
          }
        }
      });

      goalsProgressRate += miniGoalsTotal > 0 ? miniGoalsCompleted / miniGoalsTotal : 0;
      goalsScore = (goalsProgressRate / pillarGoals.length) * 0.4; // 40% do score
    }

    // === PARTE 3: BONUS DE CONSISTÊNCIA (até +10%) ===
    const consistencyBonus = consistencyScore * 0.1;

    // === SCORE FINAL (0-10) ===
    const finalScore = Math.min(10, Math.round((habitsScore + goalsScore + consistencyBonus) * 10));

    // === METADATA DETALHADA ===
    const metadata = {
      calculation_version: '2.0',
      timestamp: new Date().toISOString(),
      habits: {
        total: pillarHabits.length,
        completed: totalCompleted,
        possible: totalPossible,
        completion_rate: habitsCompletionRate,
        consistency_avg: consistencyScore
      },
      goals: {
        total: pillarGoals.length,
        mini_goals_completed: miniGoalsCompleted,
        mini_goals_total: miniGoalsTotal,
        progress_rate: miniGoalsTotal > 0 ? miniGoalsCompleted / miniGoalsTotal : 0
      },
      score_breakdown: {
        habits_contribution: habitsScore * 10,
        goals_contribution: goalsScore * 10,
        consistency_bonus: consistencyBonus * 10,
        final_score: finalScore
      }
    };

    console.log(`📊 Score calculado para ${pillar}:`, {
      score: finalScore,
      breakdown: metadata.score_breakdown
    });

    return { score: finalScore, metadata };
  },

  /**
   * Calcular e salvar pontuações da semana atual para TODOS os pilares
   */
  async calculateAndSaveWeeklyScores(
    userId: string,
    habits: any[],
    goals: any[]
  ): Promise<void> {
    try {
      const today = new Date();
      const monday = getMonday(today);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const weekStart = monday.toISOString().split('T')[0];
      const weekEnd = sunday.toISOString().split('T')[0];
      const weekDates = getWeekDates(monday);
      
      // TODOS os 6 pilares (não apenas pilares_foco)
      const ALL_PILLARS: PillarSlug[] = [
        'interior',
        'profissional', 
        'espiritual',
        'saudavel',
        'social',
        'sentimental'
      ];
      
      // Calcular pontuação inteligente para cada pilar
      const scores = ALL_PILLARS.map(pillar => {
        const { score, metadata } = this.calculatePillarScoreIntelligent(
          pillar,
          habits,
          goals,
          weekDates
        );
        
        return {
          pillar,
          score,
          metadata
        };
      });
      
      // Salvar no banco (upsert para atualizar semana atual)
      for (const { pillar, score, metadata } of scores) {
        const { error } = await supabase
          .from('weekly_pillar_scores')
          .upsert({
            user_id: userId,
            pillar,
            week_start_date: weekStart,
            week_end_date: weekEnd,
            score,
            calculation_method: 'intelligent_v2',
            metadata,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,pillar,week_start_date'
          });

        if (error) {
          console.error(`Erro ao salvar score do pilar ${pillar}:`, error);
        }
      }

      console.log('✅ Pontuações semanais salvas com sucesso');
    } catch (error) {
      console.error('❌ Erro ao calcular pontuações semanais:', error);
      throw error;
    }
  }
};

// Helper functions
function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDates(monday: Date): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

function shouldHabitRunOnDate(habit: any, dateISO: string): boolean {
  if (habit.frequency === 'diaria') return true;
  
  const date = new Date(dateISO + 'T00:00:00');
  const dayOfWeek = date.getDay();
  
  if (habit.frequency === 'semanal' && habit.days_of_week) {
    const dayNames = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
    return habit.days_of_week.includes(dayNames[dayOfWeek]);
  }
  
  if (habit.frequency === 'especifica' && habit.specific_dates) {
    return habit.specific_dates.includes(dateISO);
  }
  
  return false;
}
